import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Typography } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  listPointState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import { currentAnnotation } from 'store/krpanoStore'
import { AnnotationType } from 'types/building'
import Utils from 'utils'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'
import {
  getPointVersion,
  handlePointVersionError,
  syncPointVersion
} from 'utils/pointVersion'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

type Props = {
  setIsOpen: Dispatch<SetStateAction<string>>
  handleCloseModal: () => void
}

export default function ModalDeleteAnnotation({
  setIsOpen,
  handleCloseModal
}: Props) {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const { navigate, getBuiildingType } = useDirectory()

  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const currentSelected = useRecoilValue(currentAnnotation)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const setPoints = useSetRecoilState(listPointState)

  const { trigger: deleteAnnotation, isMutating } =
    useMutation(`v1/annotations`)
  const { trigger: deleteOldContent, isMutating: isDeletingOldContent } =
    useMutation('v1/delete-file')

  const handleClose = () => {
    setIsOpen('')
  }

  const handleSubmit = async () => {
    if (isMutating || isDeletingOldContent) return
    const krpano = new KrpanoEngine('full').instance
    try {
      if (currentSelected?.memo) {
        await deleteOldContent({
          data: {
            key: Krpano.GetMemoKeyFromPresignedUrl(currentSelected?.memo)
          }
        })
      }
      const versionIdToSend = getPointVersion(currentSelectedPoint, pointDetail)
      const { data } = await deleteAnnotation({
        method: 'delete',
        query: [currentSelected?.id],
        headers: { 'X-Point-Version-ID': String(versionIdToSend) },
        params: {
          building_id: Number(id),
          floor_id: Number(floorId),
          point_id: currentSelectedPoint
        },
        data: null,
        successMessage: t('building.deleted'),
        onError: (error) => handlePointVersionError(error, t)
      })

      syncPointVersion(
        data?.pointVersionId,
        currentSelectedPoint,
        setPointDetail,
        setPoints
      )
      setPointDetail(
        (prev) =>
          prev && {
            ...prev,
            annotations: prev.annotations.filter(
              (item) => item.id !== currentSelected?.id
            )
          }
      )

      krpano.call(`remove_annotation(${currentSelected?.id});`)
      krpano.call(`removehotspot(year-${currentSelected?.id});`)
      if (
        Number(currentSelected?.type) === AnnotationType.MEMO &&
        getBuiildingType() === 10
      ) {
        krpano.call(`remove_callout_memo_annotation(${currentSelected?.id});`)
      }
      handleCloseModal()
      handleClose()
    } catch (error) {
      if (handlePointVersionError(error, t)) {
        handleClose()
        handleCloseModal()
        return
      }
      handleClose()
      handleCloseModal()
      Utils.handleErrorNavigate(error, navigate)
    }
  }

  return (
    <Modal
      onCancel={handleClose}
      width={440}
      isEnterable
      onEnter={handleSubmit}
    >
      <Title title={t('common.titleDeleteAnnotation')} bottomMargin="mb-8" />

      <div className="flex flex-col gap-10 text-center">
        <Typography className="text-[14px] leading-[21px]">
          {t('building.deleteConfirmation')}
        </Typography>
        <div className="flex gap-10 justify-center">
          <Button sizing="w-[140px] h-[50px]" onClick={handleClose}>
            {t('building.buttonCancel')}
          </Button>

          <Button
            sizing="w-[140px] h-[50px]"
            className="bg-[#B80808]"
            onClick={handleSubmit}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
